import {useReducer} from "react";
import reducer from "./reducer";
import CharityContext, {internalState} from "./context";
import {execute} from "../../utils/helpers/execute";
import EndPoints from "../../services/end-points";
import {ICharity} from "../../models/charity/response";
import {ICreateCharity, IUpdateCharity} from "../../models/charity/request";
import {ICharityQuery} from "../../models/charity/query";

export interface IProps {
    children: React.ReactNode;
}

const CharityContextProvider: React.FC<IProps> = (props) => {
    const [state, dispatch] = useReducer(reducer, internalState)

    const getData = async () => {
        await execute({
            callback: async () => {
                dispatch({ type: "LOADING", payload: { loading: "list" } });
                const { data } = await EndPoints.charity.getAllCharities(state.query);
                dispatch({ type: "SET_LIST", payload: { list: data } });
            },
            fallback: (error) => {},
            finallyCallback: () => {
                dispatch({ type: "LOADING", payload: { loading: "list" } });
            },
            throwException: false,
        });
    };

    const getDetails = async (id: number) => {
        await execute({
            callback: async () => {
                dispatch({ type: "LOADING", payload: { loading: "details" } });
                const { data } = await EndPoints.charity.getCharity(id);
                dispatch({ type: "SET_DETAILS", payload: { details: data } });
            },
            fallback: (error) => {},
            finallyCallback: () => {
                dispatch({ type: "LOADING", payload: { loading: "details" } });
            },
            throwException: false,
        });
    };

    const setDetails = async (data?: ICharity) => {
        dispatch({ type: "SET_DETAILS", payload: { details: data } });
    };

    const createCharity = async (request: ICreateCharity) => {
        await execute({
            callback: async () => {
                dispatch({ type: "LOADING", payload: { loading: "create" } });
                await EndPoints.charity.createCharity(request);
                getData();
            },
            fallback: (error) => {},
            finallyCallback: () => {
                dispatch({ type: "LOADING", payload: { loading: "create" } });
            },
            throwException: true,
        });
    };

    const updateCharity = async (id: number, request: IUpdateCharity) => {
        await execute({
            callback: async () => {
                dispatch({ type: "LOADING", payload: { loading: "update" } });
                await EndPoints.charity.updateCharity(id, request);
                getData();
            },
            fallback: (error) => {},
            finallyCallback: () => {
                dispatch({ type: "LOADING", payload: { loading: "update" } });
            },
            throwException: true,
        });
    };

    const deleteCharity = async (id: number) => {
        await execute({
            callback: async () => {
                dispatch({ type: "LOADING", payload: { loading: "delete" } });
                await EndPoints.charity.deleteCharity(id);
                getData();
            },
            fallback: (error) => {},
            finallyCallback: () => {
                dispatch({ type: "LOADING", payload: { loading: "delete" } });
            },
            throwException: true,
        });
    };

    const setSearch = (search?: string) => {
        dispatch({ type: "SET_SEARCH", payload: { search } });
    };

    const setQuery = (query: ICharityQuery) => {
        dispatch({ type: "SET_QUERY", payload: { query } });
    };

    return (
        <CharityContext.Provider
            value={{
                ...state,
                actions: {
                    getData,
                    getDetails,
                    setDetails,
                    createCharity,
                    updateCharity,
                    deleteCharity,
                    setSearch,
                    setQuery,
                }
            }}
        >
            {props.children}
        </CharityContext.Provider>
    )
}

export default CharityContextProvider;